import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';

interface FilterBarProps {
  totalCount: number;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
  onFilterChange?: (filterType: string, value: string) => void;
  showReviewFilters?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.border}`,
    borderRadius: 8,
    marginBottom: 24,
    overflow: 'hidden',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '20px 24px 16px',
    gap: 16,
    flexWrap: 'wrap',
  },
  totalCountWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  totalCount: {
    margin: 0,
    fontSize: 24,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    lineHeight: '32px',
  },
  reviewPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.background.tertiary,
    color: theme.palette.text.secondary,
    borderRadius: 999,
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1,
    padding: '8px 16px',
  },
  dropdowns: {
    display: 'flex',
    gap: 16,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  resetButton: {
    padding: '8px',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.primary.main}`,
    borderRadius: '4px',
    color: theme.palette.primary.main,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    height: '40px',
    width: '40px',
    minWidth: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    position: 'relative',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
    },
  },
  formControl: {
    minWidth: 160,
  },
  inputLabel: {
    fontSize: 12,
    color: theme.palette.text.tertiary,
  },
  select: {
    height: 40,
    backgroundColor: theme.palette.background.paper,
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
  tabsContainer: {
    display: 'flex',
    gap: 4,
    borderTop: `1px solid ${theme.palette.border}`,
    borderBottom: `2px solid ${theme.palette.border}`,
    padding: '0 24px',
    overflowX: 'auto',
    backgroundColor: theme.palette.background.paper,
  },
  tabButton: {
    padding: '12px 24px',
    background: 'transparent',
    border: 'none',
    borderBottom: '3px solid transparent',
    color: theme.palette.text.tertiary,
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    position: 'relative',
    bottom: -2,
    textTransform: 'none',
    borderRadius: 0,
    whiteSpace: 'nowrap',
    minWidth: 'fit-content',
    '&:hover': {
      backgroundColor: theme.palette.background.tertiary,
    },
  },
  adminTab: {
    color: theme.palette.text.secondary,
  },
  activeTab: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    borderBottomColor: theme.palette.primary.main,
  },
  activeAdminTab: {
    backgroundColor: theme.palette.background.tertiary,
    color: theme.palette.text.secondary,
    fontWeight: 600,
    borderBottomColor: theme.palette.background.tertiary,
    borderRadius: '4px 4px 0 0',
  },
}));

export const FilterBar: React.FC<FilterBarProps> = ({
  totalCount,
  activeTab: parentActiveTab = 'discover',
  onTabChange,
  onFilterChange,
  showReviewFilters = false,
}) => {
  const classes = useStyles();
  const activeTab = parentActiveTab;
  const [assistantTypeFilter, setAssistantTypeFilter] = useState('All');
  const [approvalStatusFilter, setApprovalStatusFilter] = useState('All');
  const [useCaseFilter, setUseCaseFilter] = useState('All');
  const [ownerFilter, setOwnerFilter] = useState('All');
  const [lobFilter, setLobFilter] = useState('All');

  useEffect(() => {
    if (!showReviewFilters) {
      setOwnerFilter('All');
      setLobFilter('All');
      onFilterChange?.('owner', 'All');
      onFilterChange?.('lob', 'All');
    }
  }, [showReviewFilters]);

  const handleTabClick = (tab: string) => {
    onTabChange?.(tab);
  };

  const handleFilterChange = (filterType: string, value: string) => {
    if (filterType === 'assistantType') setAssistantTypeFilter(value);
    if (filterType === 'approvalStatus') setApprovalStatusFilter(value);
    if (filterType === 'useCase') setUseCaseFilter(value);
    if (filterType === 'owner') setOwnerFilter(value);
    if (filterType === 'lob') setLobFilter(value);
    onFilterChange?.(filterType, value);
  };

  const handleReset = () => {
    setAssistantTypeFilter('All');
    setApprovalStatusFilter('All');
    setUseCaseFilter('All');
    setOwnerFilter('All');
    setLobFilter('All');
    onFilterChange?.('assistantType', 'All');
    onFilterChange?.('approvalStatus', 'All');
    onFilterChange?.('useCase', 'All');
    onFilterChange?.('owner', 'All');
    onFilterChange?.('lob', 'All');
  };

  const renderSelect = (
    label: string,
    filterType: string,
    value: string,
    options: string[],
  ) => (
    <FormControl variant="outlined" size="small" className={classes.formControl}>
      <InputLabel className={classes.inputLabel}>{label}</InputLabel>
      <Select
        className={classes.select}
        value={value}
        onChange={e => handleFilterChange(filterType, e.target.value as string)}
        label={label}
      >
        {options.map(option => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Box className={classes.root}>
      <Box className={classes.topRow}>
        <Box className={classes.totalCountWrapper}>
          <Typography className={classes.totalCount} component="h2">
            Total Agents ({totalCount})
          </Typography>
          {showReviewFilters && <Box className={classes.reviewPill}>Review Queue</Box>}
        </Box>
        <Box className={classes.dropdowns}>
          {showReviewFilters &&
            renderSelect('Owner/Team', 'owner', ownerFilter, [
              'All',
              'Unknown',
              'AI Governance',
              'Research Intelligence Team',
              'Valuation Services Team',
            ])}
          {renderSelect(
            showReviewFilters ? 'Type of Agent' : 'Agent Type',
            'assistantType',
            assistantTypeFilter,
            ['All', 'Enterprise Agent', 'Personal Agent', 'Team Agent'],
          )}
          {renderSelect('Approval Status', 'approvalStatus', approvalStatusFilter, [
            'All',
            'Approved',
            'Under Review',
          ])}
          {renderSelect('Use Case', 'useCase', useCaseFilter, [
            'All',
            'Finance',
            'Leasing',
            'Research & Analytics',
            'Productivity',
            'Marketing',
            'Legal & Compliance',
          ])}
          {showReviewFilters &&
            renderSelect('LOB', 'lob', lobFilter, [
              'All',
              'Finance',
              'Leasing',
              'Valuation',
              'Advisory',
              'Capital Markets',
            ])}
          <Tooltip title="Reset filters">
            <IconButton
              className={classes.resetButton}
              onClick={handleReset}
              size="small"
              aria-label="reset filters"
            >
              <Icon fontSize="small">restart_alt</Icon>
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Box className={classes.tabsContainer}>
        <Button
          className={`${classes.tabButton} ${classes.adminTab} ${
            activeTab === 'items-for-review' ? classes.activeAdminTab : ''
          }`}
          onClick={() => handleTabClick('items-for-review')}
        >
          Items for review
        </Button>
        <Button
          className={`${classes.tabButton} ${activeTab === 'discover' ? classes.activeTab : ''}`}
          onClick={() => handleTabClick('discover')}
        >
          Discover
        </Button>
        <Button
          className={`${classes.tabButton} ${activeTab === 'bookmarks' ? classes.activeTab : ''}`}
          onClick={() => handleTabClick('bookmarks')}
        >
          Bookmarks
        </Button>
        <Button
          className={`${classes.tabButton} ${activeTab === 'trending' ? classes.activeTab : ''}`}
          onClick={() => handleTabClick('trending')}
        >
          Trending Agents
        </Button>
        <Button
          className={`${classes.tabButton} ${activeTab === 'recommended' ? classes.activeTab : ''}`}
          onClick={() => handleTabClick('recommended')}
        >
          Recommended for you
        </Button>
      </Box>
    </Box>
  );
};
